import { useState } from 'react';
import { useEmployeeUser } from './EmployeeUserContext';
import { hrProfileService } from '@/lib/hrProfileService';
import {
  User, MapPin, CreditCard, Save,
  Loader2, CheckCircle2, AlertCircle, ChevronDown, ChevronUp,
} from 'lucide-react';

function Field({ label, value, name, type = 'text', disabled = false, onChange }: {
  label: string; value: string; name: string; type?: string;
  disabled?: boolean; onChange: (name: string, value: string) => void;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-gray-500">{label}</label>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={e => onChange(name, e.target.value)}
        dir={type === 'email' || type === 'tel' ? 'ltr' : 'rtl'}
        className={[
          'w-full px-3 py-2.5 rounded-xl border text-sm transition',
          disabled
            ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
            : 'bg-white text-gray-900 border-gray-200 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent',
        ].join(' ')}
      />
    </div>
  );
}

function SelectField({ label, value, name, options, onChange }: {
  label: string; value: string; name: string;
  options: { value: string; label: string }[];
  onChange: (name: string, value: string) => void;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-gray-500">{label}</label>
      <select value={value} onChange={e => onChange(name, e.target.value)}
        className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white
                   text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-400">
        <option value="">בחר...</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export function EmployeeProfilePage() {
  const { session, updateProfile } = useEmployeeUser();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bankOpen, setBankOpen] = useState(false);

  if (!session) return null;

  const p = session.profile;

  const handleChange = (name: string, value: string) => {
    updateProfile({ [name]: value } as Record<string, string>);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    const { error: err } = await hrProfileService.updateMyProfile({
      first_name: p.firstName ?? undefined,
      last_name: p.lastName ?? undefined,
      email: p.email ?? undefined,
      phone: p.phone ?? undefined,
      date_of_birth: p.dateOfBirth ?? undefined,
      gender: p.gender ?? undefined,
      marital_status: p.maritalStatus ?? undefined,
      city: p.city ?? undefined,
      street: p.street ?? undefined,
      house_number: p.houseNumber ?? undefined,
      postal_code: p.postalCode ?? undefined,
      bank_name: p.bankName ?? undefined,
      bank_branch: p.bankBranch ?? undefined,
      bank_account_number: p.bankAccountNumber ?? undefined,
    });
    setSaving(false);
    if (err) {
      setError(err);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-2xl bg-teal-100 flex items-center justify-center">
          <User className="w-7 h-7 text-teal-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">הפרופיל שלי</h1>
          <p className="text-sm text-gray-500">פרטים אישיים — שמורים בחשבון שלך</p>
        </div>
      </div>

      {/* National ID (read-only) */}
      <div className="bg-teal-50 border border-teal-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <CreditCard className="w-4 h-4 text-teal-600" />
          <span className="text-xs font-semibold text-teal-700">תעודת זהות</span>
        </div>
        <p className="text-lg font-bold text-teal-800 tracking-wide" dir="ltr">
          {p.nationalId}
        </p>
        <p className="text-[11px] text-teal-600 mt-1">
          שדה זה אינו ניתן לשינוי — זהו המזהה הייחודי שלך
        </p>
      </div>

      {/* Personal details */}
      <section className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <User className="w-4 h-4 text-gray-500" />
          <h2 className="text-sm font-bold text-gray-900">פרטים אישיים</h2>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="שם פרטי" value={p.firstName ?? ''} name="firstName" onChange={handleChange} />
          <Field label="שם משפחה" value={p.lastName ?? ''} name="lastName" onChange={handleChange} />
        </div>

        <Field label="טלפון" value={p.phone ?? ''} name="phone" type="tel" onChange={handleChange} />
        <Field label="אימייל" value={p.email ?? ''} name="email" type="email" onChange={handleChange} />
        <Field label="תאריך לידה" value={p.dateOfBirth ?? ''} name="dateOfBirth" type="date" onChange={handleChange} />

        <div className="grid grid-cols-2 gap-3">
          <SelectField label="מין" value={p.gender ?? ''} name="gender" onChange={handleChange}
            options={[
              { value: 'זכר', label: 'זכר' },
              { value: 'נקבה', label: 'נקבה' },
              { value: 'אחר', label: 'אחר' },
            ]}
          />
          <SelectField label="מצב משפחתי" value={p.maritalStatus ?? ''} name="maritalStatus" onChange={handleChange}
            options={[
              { value: 'רווק/ה', label: 'רווק/ה' },
              { value: 'נשוי/אה', label: 'נשוי/אה' },
              { value: 'גרוש/ה', label: 'גרוש/ה' },
              { value: 'אלמן/ה', label: 'אלמן/ה' },
            ]}
          />
        </div>
      </section>

      {/* Address */}
      <section className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="w-4 h-4 text-gray-500" />
          <h2 className="text-sm font-bold text-gray-900">כתובת מגורים</h2>
        </div>
        <Field label="עיר" value={p.city ?? ''} name="city" onChange={handleChange} />
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Field label="רחוב" value={p.street ?? ''} name="street" onChange={handleChange} />
          </div>
          <Field label="מספר בית" value={p.houseNumber ?? ''} name="houseNumber" onChange={handleChange} />
        </div>
        <Field label="מיקוד" value={p.postalCode ?? ''} name="postalCode" onChange={handleChange} />
      </section>

      {/* Bank details (collapsible) */}
      <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <button
          onClick={() => setBankOpen(!bankOpen)}
          className="w-full flex items-center justify-between px-5 py-4 text-sm font-bold text-gray-900"
        >
          <div className="flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-gray-500" />
            פרטי חשבון בנק
          </div>
          {bankOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {bankOpen && (
          <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
            <Field label="שם הבנק" value={p.bankName ?? ''} name="bankName" onChange={handleChange} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="מספר סניף" value={p.bankBranch ?? ''} name="bankBranch" onChange={handleChange} />
              <Field label="מספר חשבון" value={p.bankAccountNumber ?? ''} name="bankAccountNumber" onChange={handleChange} />
            </div>
          </div>
        )}
      </section>

      {/* Save */}
      {error && (
        <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {saved && (
        <div className="flex items-center gap-2 px-3.5 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>הפרטים נשמרו בהצלחה</span>
        </div>
      )}

      <button onClick={handleSave} disabled={saving}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                   bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm
                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {saving ? 'שומר...' : 'שמור שינויים'}
      </button>

      <div className="h-4" />
    </div>
  );
}
